<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="$emit('close')">
    <div class="salary-modal">
      <div class="salary-modal-header">
        <div>
          <h2 class="salary-modal-title">Add Salary Payment - {{ monthLabel }}</h2>
          <p class="salary-modal-desc">Enter payment details for all employees. Select the employees you want to pay and enter deductions.</p>
        </div>
        <button class="close-button" @click="$emit('close')">&times;</button>
      </div>

      <div class="salary-modal-body">
        <div class="modal-top-row">
          <div class="modal-field">
            <label class="field-label">Payment Method (Bank)</label>
            <select v-model="cashBankAccountID" class="field-select">
              <option value="">Select account</option>
              <option
                v-for="account in cashBankAccounts"
                :key="account.id"
                :value="account.id"
              >
                {{ account.accountName }} ({{ account.accountType }})
              </option>
            </select>
          </div>
          <div class="modal-field">
            <label class="field-label">Payment Date</label>
            <div class="date-display">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <input v-model="paymentDate" type="date" class="date-input">
            </div>
          </div>
          <div class="modal-summary">
            <p class="summary-label">Selected: {{ selectedRecords.length }} employees</p>
            <p class="summary-value">Total: {{ formatCurrency(totalAmount) }}</p>
          </div>
        </div>

        <div v-if="paymentsLoading" class="loading-box">Loading pending salaries...</div>
        <div v-else-if="pendingRecords.length === 0" class="loading-box">No pending salary records for this month</div>
        <div v-else class="modal-table-container">
          <table class="modal-table">
            <thead>
              <tr>
                <th class="checkbox-col">
                  <input type="checkbox" :checked="selectedRecords.length === pendingRecords.length && pendingRecords.length > 0" @change="toggleAll">
                </th>
                <th>Employee</th>
                <th>Gross Salary</th>
                <th>WHT Deducted</th>
                <th>Net Salary</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="record in pendingRecords" :key="record.expenseID">
                <td class="checkbox-col">
                  <input type="checkbox" :checked="selectedRecords.includes(record.expenseID)" @change="toggleRecord(record.expenseID)">
                </td>
                <td><span class="employee-name">{{ record.employeeName }}</span></td>
                <td>{{ formatCurrency(record.basicSalary) }}</td>
                <td>
                  <input type="number" class="inline-input" :value="getWHT(record.expenseID)" @input="setWHT(record.expenseID, $event)" min="0" step="0.01">
                </td>
                <td class="net-col">{{ formatCurrency(record.basicSalary - getWHT(record.expenseID)) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="salary-modal-footer">
        <button class="btn-cancel" @click="$emit('close')">Cancel</button>
        <button class="btn-primary" @click="handleSubmit" :disabled="submitting">
          {{ submitting ? 'Processing...' : `Confirm Payment (${selectedRecords.length} employees)` }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'AddSalaryPaymentModal',
  props: {
    isOpen: { type: Boolean, required: true },
    monthLabel: { type: String, required: true },
    month: { type: Number, required: true },
    year: { type: Number, required: true },
    salaryPayments: { type: Array, default: () => [] }
  },
  data () {
    return {
      cashBankAccountID: '',
      paymentDate: new Date().toISOString().split('T')[0],
      selectedRecords: [],
      whtAmounts: {},
      submitting: false
    }
  },
  computed: {
    ...mapGetters('salary', { paymentsLoading: 'loading' }),
    cashBankAccounts () {
      return this.$store.getters['cashbank/accounts']?.accounts || this.$store.getters['cashbank/accounts'] || []
    },
    pendingRecords () {
      return this.salaryPayments.filter(p => (p.status || '').toLowerCase() === 'pending')
    },
    totalAmount () {
      return this.selectedRecords.reduce((sum, id) => {
        const record = this.pendingRecords.find(r => r.expenseID === id)
        if (!record) return sum
        return sum + (record.grossSalary - this.getWHT(id))
      }, 0)
    }
  },
  watch: {
    isOpen (val) {
      if (val) {
        this.fetchAccounts()
        this.resetForm()
        this.$nextTick(() => {
          this.selectedRecords = this.pendingRecords.map(r => r.expenseID)
          this.pendingRecords.forEach(r => {
            this.$set(this.whtAmounts, r.expenseID, 0)
          })
        })
      }
    },
    pendingRecords: {
      handler (records) {
        if (records.length > 0 && this.selectedRecords.length === 0 && this.isOpen) {
          this.selectedRecords = records.map(r => r.expenseID)
          records.forEach(r => {
            this.$set(this.whtAmounts, r.expenseID, 0)
          })
        }
      }
    }
  },
  methods: {
    ...mapActions('cashbank', ['fetchAccounts']),
    ...mapActions('salary', ['addSalaryPayment']),

    getWHT (id) {
      return this.whtAmounts[id] || 0
    },
    setWHT (id, event) {
      this.$set(this.whtAmounts, id, parseFloat(event.target.value) || 0)
    },

    toggleRecord (id) {
      const idx = this.selectedRecords.indexOf(id)
      if (idx === -1) this.selectedRecords.push(id)
      else this.selectedRecords.splice(idx, 1)
    },
    toggleAll () {
      if (this.selectedRecords.length === this.pendingRecords.length) {
        this.selectedRecords = []
      } else {
        this.selectedRecords = this.pendingRecords.map(r => r.expenseID)
      }
    },

    async handleSubmit () {
      if (!this.cashBankAccountID) {
        this.$store.dispatch('notification/showError', 'Please select a payment method')
        return
      }
      if (this.selectedRecords.length === 0) {
        this.$store.dispatch('notification/showError', 'Please select at least one employee')
        return
      }

      this.submitting = true
      try {
        const payload = {
          cashBankAccountID: parseInt(this.cashBankAccountID),
          paymentDate: new Date(this.paymentDate).toISOString(),
          employees: this.selectedRecords.map(id => ({
            expenseID: id,
            whtDeduction: this.getWHT(id),
            otherDeductions: 0
          }))
        }
        await this.addSalaryPayment(payload)
        this.$emit('paid')
        this.$emit('close')
      } catch (e) {
        // error handled in store
      } finally {
        this.submitting = false
      }
    },

    resetForm () {
      this.cashBankAccountID = ''
      this.paymentDate = new Date().toISOString().split('T')[0]
      this.selectedRecords = []
      this.whtAmounts = {}
    },

    formatCurrency (value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value || 0)
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.salary-modal {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
  max-width: 800px;
  width: 92%;
  max-height: 90vh;
  overflow-y: auto;
}

.salary-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 28px 28px 0;
}

.salary-modal-title { font-size: 20px; font-weight: 700; color: #1f2937; margin: 0 0 6px; }
.salary-modal-desc { font-size: 14px; color: #6b7280; margin: 0; }

.close-button {
  background: none; border: none; font-size: 28px;
  color: #6b7280; cursor: pointer; padding: 0; line-height: 1;
}

.salary-modal-body { padding: 24px 28px; }

.modal-top-row {
  display: flex; align-items: flex-end; gap: 24px;
  margin-bottom: 24px; flex-wrap: wrap;
}

.modal-field { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 14px; font-weight: 500; color: #374151; }

.field-select {
  padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 14px; color: #1f2937; background: white; cursor: pointer; min-width: 180px;
}

.field-select:focus {
  outline: none; border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.date-display {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; border: 1px solid #d1d5db;
  border-radius: 8px; background: white;
}

.date-input { border: none; font-size: 14px; color: #1f2937; background: transparent; outline: none; }

.modal-summary { margin-left: auto; text-align: right; }
.summary-label { font-size: 13px; color: #6b7280; margin: 0 0 2px; }
.summary-value { font-size: 24px; font-weight: 700; color: #1f2937; margin: 0; }

.loading-box {
  padding: 40px 20px; text-align: center;
  color: #6b7280; background: #f9fafb; border-radius: 8px;
}

.modal-table-container { overflow-x: auto; }
.modal-table { width: 100%; border-collapse: collapse; }

.modal-table th {
  padding: 12px; text-align: left; font-size: 13px;
  font-weight: 600; color: #6b7280; background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.modal-table td {
  padding: 12px; font-size: 14px; color: #1f2937;
  border-bottom: 1px solid #f3f4f6;
}

.checkbox-col { width: 40px; text-align: center; }
.employee-name { font-weight: 600; }
.net-col { font-weight: 700; text-align: right; }

.inline-input {
  width: 90px; padding: 6px 10px; border: 1px solid #d1d5db;
  border-radius: 6px; font-size: 14px; color: #1f2937; text-align: right;
}

.inline-input:focus {
  outline: none; border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

input[type="checkbox"] { cursor: pointer; }

.salary-modal-footer {
  display: flex; justify-content: center; gap: 12px;
  padding: 20px 28px; border-top: 1px solid #e5e7eb;
}

.btn-cancel {
  padding: 10px 24px; border: 1px solid #e5e7eb; background: white;
  color: #1f2937; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;
}

.btn-cancel:hover { background: #f3f4f6; }

.btn-primary {
  background: #2563eb; color: white; border: none; padding: 10px 24px;
  border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;
}

.btn-primary:hover { background: #1d4ed8; }
.btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }

@media (max-width: 768px) {
  .modal-top-row { flex-direction: column; align-items: stretch; }
  .modal-summary { margin-left: 0; text-align: left; }
}
</style>
